import { formatFileSize } from '@/shared/utils/format';
import { escapeHtml } from '@/shared/utils/misc';

import { uploadFileToTemp } from '@/front/utils/uploadToTemp';

export type TempAttachmentMeta = { name: string; contentType: string; size: number; tempKey: string };

type SetupTempAttachmentsOptions = {
  fileInput: HTMLInputElement;
  list: HTMLElement;
  maxSizeMb: number;
  tempUrl: string;
  onError: (message: string) => void;
  onChange?: (items: TempAttachmentMeta[]) => void;
  maxFiles?: number;
  accept?: string[];
};

export function setupTempAttachments({
  fileInput,
  list,
  maxSizeMb,
  tempUrl,
  onError,
  onChange,
  maxFiles,
  accept,
}: SetupTempAttachmentsOptions) {
  const items: TempAttachmentMeta[] = [];

  const render = () => {
    list.innerHTML = items
      .map(
        (item, index) =>
          `<div class="d-flex align-items-center gap-2 mb-1" data-index="${index}">
            <span>${escapeHtml(item.name)}</span>
            <span class="text-more-muted small">(${formatFileSize(item.size)})</span>
            <a href="#" class="remove-attachment text-danger" data-index="${index}"><i class="fa-solid fa-times"></i></a>
          </div>`,
      )
      .join('');

    list.querySelectorAll('.remove-attachment').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt((e.currentTarget as HTMLElement).dataset.index ?? '-1', 10);

        if (index >= 0) {
          items.splice(index, 1);
          render();
        }
      });
    });

    onChange?.(items);
  };

  const reset = () => {
    items.length = 0;
    render();
    fileInput.value = '';
  };

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;

    if (accept && !accept.includes(file.type)) {
      onError('This file type is not allowed.');
      fileInput.value = '';
      return;
    }

    if (maxFiles !== undefined && items.length >= maxFiles) {
      onError(`You can upload up to ${maxFiles} files.`);
      fileInput.value = '';
      return;
    }

    if (file.size > maxSizeMb * 1024 * 1024) {
      onError(`File is too large. Max size is ${maxSizeMb}MB.`);
      fileInput.value = '';
      return;
    }

    try {
      const res = await fetch(tempUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) throw new Error('Failed to get upload URL');

      const { tempKey, tempPutUrl } = (await res.json()) as { tempKey: string; tempPutUrl: string };
      const uploaded = await uploadFileToTemp(file, { tempKey, tempPutUrl });
      items.push(uploaded);
      render();
    } catch (err) {
      console.error('Error uploading file:', err);
      onError('Error uploading file');
    }

    fileInput.value = '';
  });

  render();

  return {
    getItems: () => [...items],
    reset,
  };
}
